import { DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageNavBar } from 'components/PageNavBar'
import { useEffect, useMemo, useState } from 'react'
import {
  getFirstVisibleLoanType,
  getLoanTypeMapForSelect,
  LoanType,
  loanTypeMap,
} from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup, Checkbox, Toggle, Tooltip } from 'stories/components'
import { capitalizeLettersSeparatedBySlash, confirm, prompt } from 'utils'
import { confirmOptions } from 'utils/modals/confirmOptions'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultDocumentWorkflows,
  mergeDocumentConfig,
} from '../Workflows/documentDefaults'
import { IWorkflowConfig, TMultiWorkflowConfig, TWorkflowConfig } from '../Workflows/type'
import {
  commitmentLetterFieldOptions,
  defaultCommitmentLetterFields,
} from './commitmentLetterFields'

const mergeCommitmentLetterFields = (fields?: string[] | null): string[] => {
  if (!fields || !Array.isArray(fields)) return [...defaultCommitmentLetterFields]
  return fields.filter((key) => !!commitmentLetterFieldOptions[key])
}

export const CommitmentLettersConfig = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<TMultiWorkflowConfig>()
  const [loanType, setLoanType] = useState(getFirstVisibleLoanType)
  const channelTabs = getLoanTypeMapForSelect()

  useEffect(() => {
    setLoading(true)
    getAdminConfig('workflows')
      .then((res) => setData(res))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (getLoanTypeMapForSelect()[String(loanType)] === undefined) setLoanType(getFirstVisibleLoanType())
  }, [loanType])

  const channelData = data?.[loanType]
  const documents = useMemo(
    () => mergeDocumentConfig(channelData?.documents),
    [channelData?.documents],
  )
  const selectedFields = useMemo(
    () => mergeCommitmentLetterFields(channelData?.commitmentLetterFields),
    [channelData?.commitmentLetterFields],
  )

  const saveChannel = async (next: TWorkflowConfig) => {
    if (!data) return
    setLoading(true)
    const newData = cloneDeep(data)
    newData[loanType] = next
    setData(newData)
    await setAdminConfig(`workflows/${loanType}`, next)
    setLoading(false)
  }

  const updateDocuments = async (key: string, patch: Partial<IWorkflowConfig>) => {
    if (!channelData) return
    const next = cloneDeep(channelData)
    if (!next.documents) next.documents = {}
    next.documents[key] = {
      ...documents[key],
      ...next.documents[key],
      ...patch,
    }
    await saveChannel(next)
  }

  const onChangeTitle = async (key: string) => {
    const originalTitle = documents[key]?.title || capitalizeLettersSeparatedBySlash(key)
    const newValue = await prompt(`Input title for Action - ${capitalizeLettersSeparatedBySlash(key)}`, {
      value: originalTitle,
    })
    if (!newValue) return
    await updateDocuments(key, { title: newValue })
  }

  const onToggleField = async (fieldKey: string, checked: boolean) => {
    if (!channelData) return
    const nextFields = checked
      ? [...new Set([...selectedFields, fieldKey])]
      : selectedFields.filter((k) => k !== fieldKey)
    const ordered = defaultCommitmentLetterFields.filter((k) => nextFields.includes(k))
    const next = cloneDeep(channelData)
    next.commitmentLetterFields = ordered
    await saveChannel(next)
  }

  const onCopyFromChannel = async () => {
    if (!data || !channelData) return
    const visibleChannels = getLoanTypeMapForSelect()
    const options = Object.keys(data)
      .filter((key) => key != (loanType as any) && visibleChannels[key] !== undefined)
      .map((key) => ({ name: visibleChannels[key] || loanTypeMap[key], value: Number(key) }))

    const sourceType = (await confirmOptions('Choose channel to copy from', options)) as any as LoanType
    if ((sourceType as any) === false) return

    const sourceName = loanTypeMap[String(sourceType)]
    const targetName = loanTypeMap[String(loanType)]
    const content = (
      <div className="text-gray-600 mb-4 text-md">
        Copy Commitment Letter settings from <strong>{sourceName}</strong> to <strong>{targetName}</strong>?
      </div>
    )
    const confirmed = await confirm(content)
    if (!confirmed) return

    const source = data[sourceType]
    const next = cloneDeep(channelData)
    next.documents = cloneDeep(source?.documents || {})
    next.commitmentLetterFields = mergeCommitmentLetterFields(source?.commitmentLetterFields)
    await saveChannel(next)
  }

  return (
    <div className="relative px-1 py-1.5">
      <LayoutLoading show={isLoading} />

      <div className="flex justify-between items-center mb-4">
        <ButtonGroup title={channelTabs} onChange={setLoanType} value={`${loanType}`} />
        <Tooltip message="Copy from other channel">
          <span className="btn-icon" onClick={() => onCopyFromChannel()}>
            <DocumentDuplicateIcon className="w-4 h-4" />
          </span>
        </Tooltip>
      </div>

      {channelData && (
        <>
          <div className="border-b-2 border-shade-blue w-full italic mb-6">- Documents</div>
          <PageNavBar
            titles={Object.keys(documents).map(
              (key) => documents[key]?.title || capitalizeLettersSeparatedBySlash(key),
            )}
            activeIndex={-1}
            activeIndexs={Object.keys(documents)
              .map((key, index: number) => ({ key, index }))
              .filter(({ key }) => documents[key].enabled)
              .map(({ index }) => index)}
            onMove={(index: number) => {
              const key = Object.keys(documents)[index]
              updateDocuments(key, { enabled: !documents[key].enabled })
            }}
            isAllClickable
          />
          {Object.keys(defaultDocumentWorkflows).map((key) => (
            <div className="mb-4" key={key}>
              <RenderInput
                Key={key}
                key={key}
                input={{
                  inputType: 'section',
                  title: documents[key]?.title || capitalizeLettersSeparatedBySlash(key),
                  additionalElements: (
                    <div className="flex gap-2 items-center">
                      <Toggle
                        id={key}
                        value={documents[key].enabled}
                        onChange={(value) => updateDocuments(key, { enabled: value })}
                      />
                      <span className="btn-icon" onClick={() => onChangeTitle(key)}>
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                    </div>
                  ),
                }}
                onChange={() => {}}
              />
            </div>
          ))}

          <div className="border-b-2 border-shade-blue w-full italic mb-4 mt-8">
            - Loan Information Fields
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Choose which Loan Information items appear on commitment letters for this channel.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-3xl">
            {defaultCommitmentLetterFields.map((fieldKey, index) => (
              <div
                key={fieldKey}
                className={`flex items-center p-2 pl-3 rounded ${index % 2 ? 'bg-slate-50' : ''}`}
              >
                <Checkbox
                  id={`commitment-field-${fieldKey}`}
                  title={commitmentLetterFieldOptions[fieldKey]}
                  value={selectedFields.includes(fieldKey)}
                  onChange={(checked) => onToggleField(fieldKey, checked)}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
